const INITIAL_ZOOM_LEVEL = 0.6;
const MULTI_PAGE_SEPARATOR = "<page>";
const LISTICLE_SEPARATOR = "\n";

const PROPERTY_KEYS = {
  FONT_FAMILY: "font-family",
  FONT_SIZE: "font-size",
  TEXT_ALIGN: "text-align",
  TEXT_DECORATION: "text-decoration",
  TEXT_DECORATION_COLOR: "text-decoration-color",
  TEXT_TRANSFORM: "text-transform",
  TEXT_COLOR: "text-color",
  BG_COLOR: "bg-color",
  FONT_WEIGHT: "font-weight",
  FONT_STYLE: "font-style",
  FLEX_BASIS: "flex-basis",
  WIDTH: "width",
  BORDER: "border",
  BORDER_COLOR: "border-color",
  BORDER_RADIUS: "border-radius",
  PADDING: "padding",
  GAP: "gap",
  DISPLAY: "display",
  FLEX_DIRECTION: "flex-direction",
  ALIGN_ITEMS: "align-items",
  JUSTIFY_CONTENT: "justify-content",
  OVERFLOW: "overflow",
  LEADING: "leading",
  POSITION: "position",
  BOTTOM: "bottom",
  RIGHT: "right",
  SELECT: "select",
  MARGIN: "margin",
  BORDER_STYLE: "border-style",
  BG_IMAGE: "bg-image",
  BG_SIZE: "bg-size",
  BG_REPEAT: "bg-repeat",
  BG_POSITION: "bg-position",
  LIST_STYLE_POSITION: "list-style-position",
  TEXT_DECORATION_THICKNESS: "text-decoration-thickness",
  BACKDROP_FILTER: "backdrop-filter",
  BOX_SHADOW: "box-shadow",
  TEXT_SHADOW: "text-shadow",
  OPACITY: "opacity",
};

const ELEMENT_KEYS = {
  RAW_ROOT_EDITOR: "raw-root-editor",
  TITLE: "title",
  CONTENT: "content",
  FILES: "files",
  BRAND: "brand",
  PAGINATION: "pagination",
};

const DEFAULT_ITEM_CLASSES = {
  [PROPERTY_KEYS.FONT_WEIGHT]: "font-normal",
  [PROPERTY_KEYS.FONT_STYLE]: "not-italic",
  [PROPERTY_KEYS.TEXT_COLOR]: "text-inherit",
  [PROPERTY_KEYS.TEXT_ALIGN]: "text-left",
  [PROPERTY_KEYS.TEXT_DECORATION]: "no-underline",
  [PROPERTY_KEYS.TEXT_DECORATION_COLOR]: "decoration-transparent",
  [PROPERTY_KEYS.TEXT_TRANSFORM]: "normal-case",
  [PROPERTY_KEYS.PADDING]: "p-0",
  [PROPERTY_KEYS.BG_COLOR]: "bg-transparent",
  [PROPERTY_KEYS.BORDER]: "border-none",
  [PROPERTY_KEYS.BORDER_COLOR]: "border-transparent",
  [PROPERTY_KEYS.BORDER_RADIUS]: "rounded-none",
  [PROPERTY_KEYS.MARGIN]: "m-0",
};

const HTML_TAGS_MAP = {
  code: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.BG_COLOR]: "bg-gray-800",
    [PROPERTY_KEYS.BORDER_RADIUS]: "rounded",
    [PROPERTY_KEYS.PADDING]: "p-1",
    [PROPERTY_KEYS.TEXT_COLOR]: "text-gray-500",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  strong: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.TEXT_COLOR]: "text-gray-500",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  a: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.TEXT_DECORATION]: "underline",
    [PROPERTY_KEYS.TEXT_DECORATION_COLOR]: "decoration-red-500",
    [PROPERTY_KEYS.DECORATION]: "decoration-4",
  },
  h1: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-5xl",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  h2: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-4xl",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  h3: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-3xl",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  h4: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-2xl",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  h5: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-xl",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  h6: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-sm",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
  },
  ol: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.LIST_STYLE_POSITION]: "outside",
  },
  ul: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.LIST_STYLE_POSITION]: "outside",
  },
  blockquote: {
    ...DEFAULT_ITEM_CLASSES,
  },
  table: {
    ...DEFAULT_ITEM_CLASSES,
  },
  hr: {
    [PROPERTY_KEYS.MARGIN_X]: "mx-auto",
    [PROPERTY_KEYS.MARGIN_Y]: "my-1",
    [PROPERTY_KEYS.PADDING]: "p-0",
    [PROPERTY_KEYS.WIDTH]: "w-full",
    [PROPERTY_KEYS.BORDER_COLOR]: "border-gray-700",
    [PROPERTY_KEYS.BORDER_RADIUS]: "rounded-md",
    [PROPERTY_KEYS.BORDER_STYLE]: "border-solid",
    [PROPERTY_KEYS.BORDER]: "border",
  },
};

const DEFAULT_CLASSES = {
  ...HTML_TAGS_MAP,
  [ELEMENT_KEYS.RAW_ROOT_EDITOR]: {
    [PROPERTY_KEYS.BG_COLOR]: "bg-gray-900", // prev bg-[#202227]
    [PROPERTY_KEYS.POSITION]: "relative",
    [PROPERTY_KEYS.GAP]: "gap-6",
    [PROPERTY_KEYS.DISPLAY]: "flex",
    [PROPERTY_KEYS.FLEX_DIRECTION]: "flex-col",
    [PROPERTY_KEYS.ALIGN_ITEMS]: "items-center",
    [PROPERTY_KEYS.JUSTIFY_CONTENT]: "justify-center",
    [PROPERTY_KEYS.SELECT]: "select-none",
    [PROPERTY_KEYS.OVERFLOW]: "overflow-hidden",
    [PROPERTY_KEYS.FONT_SIZE]: "text-md",
    [PROPERTY_KEYS.TEXT_COLOR]: "text-white",
    [PROPERTY_KEYS.TEXT_ALIGN]: "text-left",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-normal",
    [PROPERTY_KEYS.FONT_FAMILY]: "font-space-mono",
  },
  [ELEMENT_KEYS.PAGINATION]: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "font-sm",
    [PROPERTY_KEYS.DECORATION]: "decoration-4",
    [PROPERTY_KEYS.POSITION]: "absolute",
    [PROPERTY_KEYS.BOTTOM]: "bottom-4",
    [PROPERTY_KEYS.RIGHT]: "right-12",
  },
  [ELEMENT_KEYS.TITLE]: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-4xl",
    [PROPERTY_KEYS.FONT_WEIGHT]: "font-bold",
    [PROPERTY_KEYS.TEXT_ALIGN]: "text-center",
    [PROPERTY_KEYS.WIDTH]: "w-4/5",
    [PROPERTY_KEYS.PADDING]: "p-0",
  },
  [ELEMENT_KEYS.CONTENT]: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-2xl",
    [PROPERTY_KEYS.WIDTH]: "w-4/5",
    [PROPERTY_KEYS.PADDING]: "p-0",
    [PROPERTY_KEYS.DISPLAY]: "flex",
    [PROPERTY_KEYS.FLEX_DIRECTION]: "flex-col",
    [PROPERTY_KEYS.ALIGN_ITEMS]: "flex-start",
    [PROPERTY_KEYS.JUSTIFY_CONTENT]: "justify-center",
    [PROPERTY_KEYS.GAP]: "gap-6",
  },
  [ELEMENT_KEYS.FILES]: {
    [PROPERTY_KEYS.BORDER]: "border-none",
    [PROPERTY_KEYS.BORDER_RADIUS]: "rounded-xl",
    [PROPERTY_KEYS.OVERFLOW]: "overflow-hidden",
    [PROPERTY_KEYS.WIDTH]: "w-4/5",
  },
  [ELEMENT_KEYS.BRAND]: {
    ...DEFAULT_ITEM_CLASSES,
    [PROPERTY_KEYS.FONT_SIZE]: "text-sm",
    [PROPERTY_KEYS.TEXT_COLOR]: "text-white",
  },
};

const HTML_TAGS_LIST = Object.keys(HTML_TAGS_MAP);
const HTML_CLASSES_LIST = Object.values(ELEMENT_KEYS).map((key) => `.${key}`);

export {
  PROPERTY_KEYS,
  DEFAULT_ITEM_CLASSES,
  ELEMENT_KEYS,
  HTML_TAGS_LIST,
  DEFAULT_CLASSES,
  HTML_CLASSES_LIST,
  INITIAL_ZOOM_LEVEL,
  MULTI_PAGE_SEPARATOR,
  LISTICLE_SEPARATOR,
};
